import React, { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  BookOpen, Brain, BarChart2, Database,
  GitBranch, MessageCircle, Menu, X,
  Activity, Cpu, Layers,
  ChevronDown, ChevronRight, Lock, CheckCircle, Play, Star,
  User, LogOut, Sparkles, Bot, Network,
  Youtube, Award, ShieldCheck
} from 'lucide-react'
import { useAppStore } from '../store'
import { useAuthStore } from '../store/authStore'
import LevelUpPopup from './LevelUpPopup'
import {
  ALL_CHAPTERS, DOMAIN_LABELS, DOMAIN_EMOJIS, GRADE_SHORT,
  type Grade,
} from '../data/syllabus'
import clsx from 'clsx'

// ── AI 도구 섹션 정의 ─────────────────────────────────────────────────────────
const AI_TOOL_SECTIONS = [
  {
    group: '🤖 Agentic AI 학습 도구',
    desc: '학습자 상태를 실시간 분석해 개인 맞춤 피드백 제공',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    items: [
      { to: '/tutor',  label: 'AI 튜터',    icon: Brain,         agentRole: '오개념 자동 진단 · 단계별 피드백', mcpRole: null },
      { to: '/graph',  label: '그래프 탐색', icon: BarChart2,     agentRole: '기울기·절편 변화 실시간 분석',    mcpRole: null },
      { to: '/chat',   label: 'AI 채팅',     icon: MessageCircle, agentRole: '자유 질문 → 온톨로지 기반 답변',  mcpRole: null },
    ],
  },
  // 🔌 MCP 연동 도구 섹션은 관리자/개발자 전용 — 학습자 메뉴에서 숨김
  // { group: 'MCP 연동 도구', items: ['/mcp', '/ontology', '/rules'] }
]

// 크리에이터 & 영상 섹션
const CREATOR_SECTIONS = [
  {
    group: '🎬 크리에이터 허브',
    desc: '영상 등록 · 수익 · 관리',
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    items: [
      { to: '/video-submit',       label: '영상 등록',       icon: Youtube,      desc: '5분 개념 영상 업로드' },
      { to: '/creator-dashboard',  label: '크리에이터 대시보드', icon: Award,     desc: '조회수 · 크레딧 현황' },
    ],
  },
]

// 관리자 전용 메뉴 (isAdmin=true 일 때만 표시)
const ADMIN_SECTIONS = [
  {
    group: '🔐 관리자 도구',
    desc: 'MCP 콘솔 · 온톨로지 · 규칙 엔진 (admin 전용)',
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    items: [
      { to: '/video-review',label: '영상 검수',  icon: ShieldCheck, desc: '크리에이터 영상 승인/거부' },
      { to: '/mcp',      label: 'MCP 콘솔',  icon: Cpu,       desc: 'AI 에이전트 도구 실행 현황' },
      { to: '/ontology', label: '온톨로지',   icon: Database,  desc: 'OWL 클래스 & 속성 시각화' },
      { to: '/rules',    label: '규칙 엔진',  icon: GitBranch, desc: 'SWRL 규칙 18개 추론 현황' },
      { to: '/mcp-info', label: 'MCP 안내',   icon: Network,   desc: 'MCP 연동 가이드' },
    ],
  },
]

const STATUS_ICON_MAP = {
  completed:   CheckCircle,
  in_progress: Play,
  available:   Star,
  locked:      Lock,
}

const GRADE_KEYS: Grade[] = [1, 2, 3]
const GRADE_EMOJI: Record<Grade, string> = { 1: '🌱', 2: '🌿', 3: '🌳' }

interface LayoutProps { children: React.ReactNode }

export default function Layout({ children }: LayoutProps) {
  const { sidebarOpen, toggleSidebar, student, currentStepId } = useAppStore()
  const { session, isLoggedIn, isAdmin, logout } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()

  const [showAiTools, setShowAiTools]   = useState(true)
  const [showMcpTools, setShowMcpTools] = useState(false)

  const unitMatch    = location.pathname.match(/^\/unit\/(.+)$/)
  const activeUnitId = unitMatch ? unitMatch[1] : null

  const defaultGrade = ALL_CHAPTERS.find(ch => ch.units.some(u => u.isCurrent))?.grade ?? 2
  const [openGrades, setOpenGrades] = useState<Record<Grade, boolean>>({
    1: defaultGrade === 1,
    2: defaultGrade === 2,
    3: defaultGrade === 3,
  })
  const toggleGrade = (g: Grade) => setOpenGrades(prev => ({ ...prev, [g]: !prev[g] }))

  const handleLogout = () => { logout(); navigate('/auth') }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className={clsx(
        'bg-white border-r border-slate-200 flex flex-col transition-all duration-300 z-20 flex-shrink-0',
        sidebarOpen ? 'w-72' : 'w-16'
      )}>

        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">f(x)</span>
          </div>
          {sidebarOpen && (
            <div className="overflow-hidden flex-1">
              <p className="text-sm font-bold text-slate-800 leading-tight">중학 수학 AI</p>
              <p className="text-xs text-slate-500">Math Ontology Tutor</p>
            </div>
          )}
          <button onClick={toggleSidebar} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* 로그인 사용자 정보 */}
        {sidebarOpen && (
          <div className="px-4 py-3 border-b border-slate-100 flex-shrink-0">
            {isLoggedIn && session ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {session.user.name.slice(0, 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate">{session.user.name}님</p>
                    <p className="text-xs text-slate-400">중{session.user.grade}학년</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-1.5 text-xs text-red-500 border border-red-200 rounded-lg py-1.5 hover:bg-red-50 transition-colors"
                >
                  <LogOut size={13} /> 로그아웃
                </button>
              </div>
            ) : (
              <NavLink to="/auth" className="flex items-center gap-2 text-xs text-primary-600 font-semibold hover:underline">
                <User size={14} /> 로그인 / 회원가입
              </NavLink>
            )}
          </div>
        )}

        {/* Navigation (scrollable) */}
        <nav className="flex-1 overflow-y-auto scroll-hide py-2 space-y-0.5">

          {/* 대시보드 */}
          <NavLink
            to="/" end
            className={({ isActive }) => clsx(
              'flex items-center gap-3 mx-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
              isActive ? 'bg-primary-100 text-primary-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
            )}
          >
            <Activity size={18} className="flex-shrink-0" />
            {sidebarOpen && <span>대시보드</span>}
          </NavLink>

          {/* ── 📚 전체 커리큘럼 섹션 ───────────────────────────────────── */}
          <div className="mx-2">
            {sidebarOpen && (
              <div className="px-3 pt-3 pb-1">
                <div className="flex items-center gap-1.5">
                  <Layers size={12} className="text-slate-400" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">📚 커리큘럼</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5 leading-tight">
                  중1~중3 과정별 개념 학습 · 문제풀이 · 실전TEST
                </p>
              </div>
            )}

            <NavLink
              to="/syllabus"
              className={({ isActive }) => clsx(
                'flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 mt-0.5',
                isActive && !activeUnitId ? 'bg-primary-100 text-primary-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
              )}
            >
              <Layers size={16} className="flex-shrink-0" />
              {sidebarOpen && <span className="text-xs flex-1">전체 커리큘럼 보기</span>}
            </NavLink>

            {/* 계층형: 학년 > 영역 > 유닛 */}
            {sidebarOpen && (
              <div className="mt-1 space-y-0.5">
                {GRADE_KEYS.map(grade => {
                  const chapters  = ALL_CHAPTERS.filter(ch => ch.grade === grade)
                  const hasActive = chapters.some(ch => ch.units.some(u => u.id === activeUnitId || u.isCurrent))
                  return (
                    <div key={grade}>
                      <button
                        onClick={() => toggleGrade(grade)}
                        className={clsx(
                          'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-colors',
                          hasActive ? 'text-primary-700 bg-primary-50' : 'text-slate-500 hover:bg-slate-50'
                        )}
                      >
                        <span>{GRADE_EMOJI[grade]}</span>
                        <span className="flex-1 text-left">{GRADE_SHORT[grade]}</span>
                        {openGrades[grade] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>

                      {openGrades[grade] && (
                        <div className="ml-3 border-l-2 border-slate-100 pl-2 space-y-0.5 pb-1">
                          {chapters.map(chapter => (
                            <div key={chapter.id}>
                              <div className="flex items-center gap-1.5 px-2 py-1 mt-1">
                                <span className="text-base leading-none">{DOMAIN_EMOJIS[chapter.domain]}</span>
                                <span className={`text-xs font-bold ${chapter.color.accent} truncate`}>
                                  {DOMAIN_LABELS[chapter.domain]}
                                </span>
                              </div>
                              {chapter.units.map(unit => {
                                const isActive   = unit.id === activeUnitId
                                const StatusIcon = STATUS_ICON_MAP[unit.status]
                                return (
                                  <NavLink
                                    key={unit.id}
                                    to={unit.contentReady ? `/unit/${unit.id}` : '/syllabus'}
                                    className={clsx(
                                      'flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all',
                                      isActive
                                        ? `${chapter.color.bg} ${chapter.color.border} border font-bold text-slate-800`
                                        : unit.status === 'locked'
                                          ? 'text-slate-300 cursor-default'
                                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                                    )}
                                  >
                                    <span>{unit.emoji}</span>
                                    <span className="flex-1 truncate leading-tight">{unit.title}</span>
                                    {unit.contentReady
                                      ? <ChevronRight size={10} className={chapter.color.accent} />
                                      : <StatusIcon size={11} className={
                                          unit.status === 'completed'   ? 'text-emerald-400' :
                                          unit.status === 'in_progress' ? 'text-blue-400'    :
                                          unit.status === 'available'   ? 'text-orange-300'  : 'text-slate-200'
                                        } />
                                    }
                                  </NavLink>
                                )
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* ── AI 도구 섹션들 ──────────────────────────────────────────── */}
          {AI_TOOL_SECTIONS.map((section, si) => {
            const isOpen   = si === 0 ? showAiTools  : showMcpTools
            const setOpen  = si === 0 ? setShowAiTools : setShowMcpTools
            const isGroupActive = section.items.some(i => location.pathname.startsWith(i.to))

            return (
              <div key={si} className="mx-2 mt-1">
                {sidebarOpen ? (
                  <>
                    {/* 섹션 헤더 */}
                    <button
                      onClick={() => setOpen(!isOpen)}
                      className={clsx(
                        'w-full text-left px-3 pt-3 pb-2 rounded-xl transition-colors',
                        isGroupActive ? section.bgColor : 'hover:bg-slate-50'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-bold ${section.color}`}>{section.group}</span>
                        {isOpen ? <ChevronDown size={12} className="text-slate-400" /> : <ChevronRight size={12} className="text-slate-400" />}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5 leading-tight">{section.desc}</p>
                    </button>

                    {/* 도구 목록 */}
                    {isOpen && (
                      <div className="mt-1 space-y-0.5">
                        {section.items.map(({ to, label, icon: Icon, agentRole, mcpRole }) => (
                          <NavLink
                            key={to}
                            to={to}
                            className={({ isActive }) => clsx(
                              'block px-3 py-2 rounded-xl transition-all duration-150',
                              isActive
                                ? `${section.bgColor} border ${section.borderColor}`
                                : 'hover:bg-slate-50'
                            )}
                          >
                            {({ isActive }) => (
                              <>
                                <div className="flex items-center gap-2">
                                  <Icon size={15} className={clsx('flex-shrink-0', isActive ? section.color : 'text-slate-500')} />
                                  <span className={clsx('text-xs font-semibold', isActive ? section.color : 'text-slate-700')}>{label}</span>
                                </div>
                                {/* Agentic AI / MCP 역할 설명 */}
                                <div className="mt-1 ml-5 space-y-0.5">
                                  <div className="flex items-center gap-1">
                                    <Bot size={9} className="text-purple-400 flex-shrink-0" />
                                    <span className="text-xs text-slate-400 leading-tight">{agentRole}</span>
                                  </div>
                                  {mcpRole && (
                                    <div className="flex items-center gap-1">
                                      <Network size={9} className="text-indigo-400 flex-shrink-0" />
                                      <span className="text-xs text-slate-400 leading-tight">{mcpRole}</span>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  /* 접힌 상태: 아이콘만 */
                  <div className="space-y-0.5">
                    {section.items.map(({ to, icon: Icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) => clsx(
                          'flex items-center justify-center py-2.5 rounded-xl transition-colors',
                          isActive ? section.bgColor : 'hover:bg-slate-100'
                        )}
                      >
                        {({ isActive }) => (
                          <Icon size={16} className={clsx(isActive ? section.color : 'text-slate-500')} />
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
          {/* ── 크리에이터 섹션 (모든 사용자) ───────────────────────────── */}
          {CREATOR_SECTIONS.map((section, si) => (
            <div key={`creator-${si}`} className="mx-2 mt-1">
              {sidebarOpen ? (
                <>
                  <div className="px-3 pt-3 pb-2">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`text-xs font-bold ${section.color}`}>{section.group}</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-tight">{section.desc}</p>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {section.items.map(({ to, label, icon: Icon, desc }) => (
                      <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) => clsx(
                          'block px-3 py-2 rounded-xl transition-all duration-150',
                          isActive
                            ? `${section.bgColor} border ${section.borderColor}`
                            : 'hover:bg-slate-50'
                        )}
                      >
                        {({ isActive }) => (
                          <div className="flex items-center gap-2">
                            <Icon size={15} className={clsx('flex-shrink-0', isActive ? section.color : 'text-slate-500')} />
                            <div>
                              <span className={clsx('text-xs font-semibold', isActive ? section.color : 'text-slate-700')}>{label}</span>
                              <p className="text-xs text-slate-400 leading-tight">{desc}</p>
                            </div>
                          </div>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-0.5">
                  {section.items.map(({ to, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) => clsx(
                        'flex items-center justify-center py-2.5 rounded-xl transition-colors',
                        isActive ? section.bgColor : 'hover:bg-slate-100'
                      )}
                    >
                      {({ isActive }) => (
                        <Icon size={16} className={clsx(isActive ? section.color : 'text-slate-500')} />
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* ── 관리자 전용 섹션 (isAdmin=true 일 때만) ─────────────────── */}
          {isAdmin && ADMIN_SECTIONS.map((section, si) => (
            <div key={`admin-${si}`} className="mx-2 mt-1">
              {sidebarOpen ? (
                <>
                  <div className="px-3 pt-3 pb-2">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`text-xs font-bold ${section.color}`}>{section.group}</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-tight">{section.desc}</p>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {section.items.map(({ to, label, icon: Icon, desc }) => (
                      <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) => clsx(
                          'block px-3 py-2 rounded-xl transition-all duration-150',
                          isActive
                            ? `${section.bgColor} border ${section.borderColor}`
                            : 'hover:bg-slate-50'
                        )}
                      >
                        {({ isActive }) => (
                          <div className="flex items-center gap-2">
                            <Icon size={15} className={clsx('flex-shrink-0', isActive ? section.color : 'text-slate-500')} />
                            <div>
                              <span className={clsx('text-xs font-semibold', isActive ? section.color : 'text-slate-700')}>{label}</span>
                              <p className="text-xs text-slate-400 leading-tight">{desc}</p>
                            </div>
                          </div>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-0.5">
                  {section.items.map(({ to, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) => clsx(
                        'flex items-center justify-center py-2.5 rounded-xl transition-colors',
                        isActive ? section.bgColor : 'hover:bg-slate-100'
                      )}
                    >
                      {({ isActive }) => (
                        <Icon size={16} className={clsx(isActive ? section.color : 'text-slate-500')} />
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        {sidebarOpen && (
          <div className="p-4 border-t border-slate-100 flex-shrink-0">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-2.5 border border-purple-100 mb-2">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={12} className="text-purple-500" />
                <span className="text-xs font-bold text-purple-700">Agentic AI 활성</span>
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse ml-auto" />
              </div>
              <div className="flex items-center gap-1.5">
                <Network size={10} className="text-indigo-400" />
                <span className="text-xs text-indigo-600 font-medium">MCP 연결됨</span>
              </div>
              <p className="text-xs text-slate-400 mt-1 leading-tight">온톨로지·규칙엔진 실시간 추론 중</p>
            </div>
            <p className="text-xs text-slate-400">Operational Math Ontology v1.0</p>
          </div>
        )}
        {!sidebarOpen && (
          <div className="p-2 border-t border-slate-100 flex-shrink-0 flex justify-center">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          </div>
        )}
      </aside>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto min-w-0">
        {children}
      </main>

      {/* ── RPG 레벨업 팝업 (전역) ───────────────────────────────────────── */}
      <LevelUpPopup />
    </div>
  )
}
